/**
 * Personalisation live preview (MFYEB-100).
 *
 * Renders the customer's name onto a base product image in the selected font and colour.
 *
 * This is the NEW personalisation UI. It coexists with the existing inline widget
 * (personalisation-widget.liquid / .js) and adoption is per product — the theme picks one
 * or the other from the `custom.personalisation_ui` product metafield. Nothing here
 * modifies or depends on the old widget.
 *
 * Rendering uses inline SVG rather than DOM-over-image or canvas because:
 *  - the viewBox gives us the normalised coordinate system the config already stores, so
 *    one set of coordinates works at every breakpoint with no JS resize handling;
 *  - rotation, alignment and baseline are native SVG;
 *  - the scrolled collapse on mobile is just a viewBox crop; the preview is always pinned.
 *
 * Font families are namespaced `pim-<code>` — this MUST match the existing widget's
 * `fontCssFamily()`, because a font registered under one name is not reachable under
 * another and both UIs load from the same PIM font set.
 */
window.Eurus = window.Eurus || { loadedScript: new Set() };

if (!window.Eurus.loadedScript.has('personalisation-preview.js')) {
  window.Eurus.loadedScript.add('personalisation-preview.js');

  document.addEventListener('alpine:init', () => {
    Alpine.data('xPersonalisationPreview', () => ({
      // --- config / lifecycle ---
      configUrl: null,
      productId: null,
      variantId: null,
      /** Selected variant is buyable. Drives the entry button; kept current in watchVariantChanges. */
      variantAvailable: true,
      config: null,
      preview: null,
      previewStep: null,

      /**
       * Which VIEW of the product is on screen: the front, a close-up of the handle.
       *
       * Null means "the primary one", resolved through the shared module so a config that predates
       * views (no `views` array) behaves exactly as it did — it yields a single synthetic view built
       * from the step-level fields.
       *
       * The primary view is what the cart, the confirmation email and the stored order image draw,
       * so a customer who never touches the dots sees what those surfaces will show.
       */
      activeViewKey: null,

      /** Bundle step on screen (MFYEB-186); null on a simple product. */
      activeStepKey: null,
      /** step key -> chosen font / colour code. `font` and `fontColour` mirror the active step. */
      stepFonts: {},
      stepColours: {},
      /** Reported by engine-bundle.js; null means every step applies. */
      bundleState: null,
      /** Set when Add to Bag was pressed before the bundle's own options were chosen. */
      bundleOptionsError: false,
      fontsReady: false,
      // Font codes whose file was recorded in the config but would not actually load. Excluded
      // from fontChoices() so the picker never offers a typeface that cannot be drawn.
      unloadableFonts: [],

      /**
       * Config lifecycle. Three states, and the distinction is the point:
       *   loading  - a config request is in flight; only ever shown INSIDE the modal.
       *   ready    - config loaded and drawable; the modal's controls are gated on this.
       *   fallback - gave up, and handed this product back to the legacy inline widget.
       *
       * The PDP entry point is gated on NONE of them. It is static text plus a button, so it
       * renders server-side and is visible at first paint. Gating it on the fetch, which is
       * what this used to do, put a spinner where the button belongs and let a failed fetch
       * offer the customer no personalisation at all.
       */
      loading: false,
      ready: false,
      fallback: false,

      // --- customer selections ---
      text: '',
      /**
       * Per-line values, keyed by text_line_key, for steps with more than one line.
       *
       * `text` stays the PRIMARY line's value: pricing, the add-to-cart guard and any placement
       * with no `text_line_key` read it, and every single-line product only ever had it. So the
       * primary line lives in `text` and this holds the rest — one value in one place, rather than
       * the same value in two that can drift.
       *
       * It was declared and passed to the geometry as `texts` from the start and never written to,
       * which is the whole reason a two-placement product drew the same name twice.
       */
      textValues: {},
      /** option_key -> chosen design `value`, for design placements. */
      designSelections: {},
      font: null,
      fontColour: null,

      // --- preview presentation ---
      collapsed: false,

      // Latched when collapsing proved to make the pane unscrollable. See onPaneScroll().
      _collapseUnstable: false,

      // When the last collapse happened, so a clamp back to the top can be told from a real
      // scroll-up. See onPaneScroll().
      _collapsedAt: 0,

      // --- modal chrome ---
      open: false,
      /**
       * The "don't miss free personalisation" interstitial (Figma PDP_modal).
       *
       * Only ever shown for a product whose personalisation is OPTIONAL, on the first attempt to
       * add to bag with nothing typed. A required product cannot reach it: there the entry button
       * IS the add-to-bag button, so there is nothing to remind anyone about.
       */
      reminderOpen: false,
      /**
       * Latched once the customer has answered the reminder, either way.
       *
       * Without it, Skip would be intercepted by the very guard it is trying to satisfy: the click
       * it forwards to the theme's button re-enters validateForAddToCart and reopens the dialog.
       * Deliberately per page-view rather than persisted, because the point is to ask once, not to
       * remember a preference.
       */
      reminderAnswered: false,
      /**
       * Per-line "this required field is empty", keyed by text_line_key.
       *
       * A map rather than one flag because a product can ask for two names and only one of them be
       * missing; a single flag would put the message under both fields.
       */
      validationErrors: {},
      /** Set when the last keystroke contained an emoji, which was removed. */
      emojiError: false,
      /** Which line that keystroke was in, so the message appears under the right field. */
      emojiErrorKey: null,

      // --- pricing (seeded from Liquid, which already knows the money format) ---
      /**
       * Storefront locale, in the same ISO form the legacy widget reads, so the two resolve the
       * SAME field labels out of the config's translations block.
       */
      locale: null,
      moneyFormat: null,
      itemPriceCents: 0,
      compareAtCents: 0,
      currencyCode: 'GBP',
      /**
       * Markets that give personalisation away whatever the PIM price says (Ireland today).
       *
       * The rule lives in Liquid, in `personalisation-free-market-boolean`, and this reads the
       * SAME snippet the legacy widget reads, so the two UIs cannot disagree about whether a
       * customer is charged. Without it the new UI prices personalisation the old one gives
       * away, and writes a `_personalisation_total_modifier` the cart transform then charges.
       */
      freePersonalisation: false,
      sectionId: null,
      variantPrices: {},
      /**
       * Every customer-visible string, seeded from data attributes in the snippet.
       *
       * NOT built as `json` literals inside Alpine expressions. Shopify's `t` filter HTML-escapes
       * its output, so `{{ key | t | json | escape }}` escaped twice and the customer read
       * "Don&#39;t miss" and "Personalise &amp; Add to Bag". A data attribute escapes exactly
       * once, which is what an attribute wants, and `dataset` decodes it on the way in.
       */
      labels: {
        fontStyle: 'Font Style',
        fontColour: 'Font Colour',
        entryFree: '',
        entryPaid: '',
        entryRequired: '',
        reminderTitle: '',
        reminderTitlePaid: '',
        reminderBody: '',
        reminderBodyPaid: '',
        name: 'Name',
        namePlaceholder: '',
        characters: 'characters',
        summaryFree: 'FREE',
      },

      get geometry() {
        return window.PimPreviewGeometry;
      },

      // Synchronous on purpose: the config load is kicked off at the end and NOT awaited, so
      // nothing the customer can see is gated on it.
      init() {
        const el = this.$el;
        this.configUrl = el.dataset.configUrl || null;
        this.productId = el.dataset.productId || null;
        this.variantId = el.dataset.variantId || null;
        // Server's answer for first paint, so a sold-out selection never flashes an entry
        // button. The theme's own buttons take over once they exist to be read.
        this.variantAvailable = el.dataset.variantAvailable !== 'false';
        this.locale = el.dataset.locale || null;
        this.moneyFormat = el.dataset.moneyFormat || null;
        this.itemPriceCents = parseInt(el.dataset.itemPrice || '0', 10) || 0;
        this.compareAtCents = parseInt(el.dataset.compareAtPrice || '0', 10) || 0;
        this.currencyCode = el.dataset.currency || 'GBP';
        this.freePersonalisation = el.dataset.freePersonalisation === 'true';
        this.sectionId = el.dataset.sectionId || null;
        // Scoped by product in the markup: a PDP and a quick view can share a page, and a
        // duplicate path id would make one preview's name follow the other's curve.
        const layerRoot = el.querySelector('[data-arc-id-base]');
        this.arcPathIdBase = (layerRoot && layerRoot.dataset.arcIdBase) || 'pim-arc';
        if (el.dataset.labelFontStyle) this.labels.fontStyle = el.dataset.labelFontStyle;
        if (el.dataset.labelFontColour) this.labels.fontColour = el.dataset.labelFontColour;
        if (el.dataset.labelEntryFree) this.labels.entryFree = el.dataset.labelEntryFree;
        if (el.dataset.labelEntryPaid) this.labels.entryPaid = el.dataset.labelEntryPaid;
        if (el.dataset.labelEntryRequired) this.labels.entryRequired = el.dataset.labelEntryRequired;
        if (el.dataset.labelReminderTitle) this.labels.reminderTitle = el.dataset.labelReminderTitle;
        if (el.dataset.labelReminderTitlePaid) this.labels.reminderTitlePaid = el.dataset.labelReminderTitlePaid;
        if (el.dataset.labelReminderBody) this.labels.reminderBody = el.dataset.labelReminderBody;
        if (el.dataset.labelReminderBodyPaid) this.labels.reminderBodyPaid = el.dataset.labelReminderBodyPaid;
        if (el.dataset.labelName) this.labels.name = el.dataset.labelName;
        if (el.dataset.labelNamePlaceholder) this.labels.namePlaceholder = el.dataset.labelNamePlaceholder;
        if (el.dataset.labelCharacters) this.labels.characters = el.dataset.labelCharacters;
        if (el.dataset.labelSummaryFree) this.labels.summaryFree = el.dataset.labelSummaryFree;

        // Prices for every variant, so the summary stays correct when the customer changes
        // size. Seeding only the initial variant leaves Item/Subtotal stale after a switch.
        try {
          this.variantPrices = JSON.parse(el.dataset.variantPrices || '{}');
        } catch (e) {
          this.variantPrices = {};
        }
        this.applyVariantPricing();

        // Fire and forget. Nothing rendered waits on it, but starting it now rather than on
        // the first click means the modal opens with data already there, an undrawable config
        // swaps to the legacy widget before the customer touches anything, and the
        // add-to-cart guard installs as early as it does today. Same single request the
        // legacy widget makes on every PDP, so page weight is unchanged.
        this.ensureConfig();
      },

      /**
       * One load per component, whichever of init and openModal asks first.
       *
       * A settled load leaves exactly one of `ready` or `fallback` true. If neither is set and
       * nothing is in flight, the previous attempt was lost rather than finished, so the memoised
       * promise will never touch this instance again and awaiting it would hang the modal open and
       * empty forever. Observed for real when a section re-render replaced the component while its
       * load was running. Start again instead.
       */
      ensureConfig() {
        const lost = this._configPromise && !this.loading && !this.ready && !this.fallback;
        if (lost) this._configPromise = null;
        if (!this._configPromise) this._configPromise = this.loadConfig();
        return this._configPromise;
      },

      async loadConfig() {
        this.loading = true;

        try {
          if (!this.configUrl) return this.handOverToWidget('no config url');

          let payload;
          try {
            // ON A DEADLINE, and the reason is not politeness. Everything that makes this
            // component safe happens AFTER this await: guardAddToCart() below, and the
            // handOverToWidget() calls that fall back to the legacy form. A bare fetch against a
            // CDN that accepts the connection and then never answers reaches neither, so the
            // customer got a permanent spinner, no form, and no add-to-cart guard - which let a
            // product whose personalisation is REQUIRED be bought with no name. A hang has to
            // become a failure, because a failure is already handled.
            const res = await window.PimConfig.fetchWithDeadline(this.configUrl, {
              headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            payload = await res.json();
          } catch (e) {
            // An abort reports itself as an AbortError with an unhelpful message, so name the
            // timeout explicitly: this string is what the cart-line diagnostics and the
            // validation guide tell people to read.
            const why =
              e && e.name === 'AbortError'
                ? 'config fetch timed out after ' + window.PimConfig.CONFIG_TIMEOUT_MS + 'ms'
                : 'config fetch failed (' + e.message + ')';
            return this.handOverToWidget(why);
          }

          // TWO CONFIG SHAPES EXIST, and this component only understands one of them.
          // The CDN file (`{cdn}configs/product-<id>.json`) carries `options` as an OBJECT of
          // `text_lines` / `fonts` / `font_colours`, which is what every accessor below reads.
          // The live PIM endpoint (`{pim}personalisation/<id>`) returns `options` as a flat
          // ARRAY of `{type, key, choices}`; the legacy widget prefers that endpoint and
          // normalises the CDN form into it (`transformCDNData`). Pointed at the PIM shape this
          // component degrades SILENTLY: no text line, no fonts, no colours, the browser's
          // default serif, and `requiresPersonalisation()` false, which quietly disarms the
          // add-to-cart guard. So refuse it loudly instead.
          if (Array.isArray(payload.options)) {
            return this.handOverToWidget('config is in the PIM array shape, not the CDN object shape');
          }

          this.config = payload;
          this.preview = payload.preview || null;
          if (!this.preview || this.preview.enabled === false) {
            return this.handOverToWidget('config carries no enabled preview block');
          }

          this.previewStep = this.geometry.resolveStep(this.preview, null);
          this.readBundleState();
          this.syncActiveStep();
          // Can itself hand over, when the product's only font has no usable file.
          this.applyDefaults();
          if (this.fallback) return;

          if (!this.renderable) {
            return this.handOverToWidget('step has no placement, or no base image');
          }

          // A text placement whose text line is missing from `options` leaves us unable to
          // read `required` or `char_limit`, so the add-to-cart guard would pass a product
          // that cannot be made and the input would accept any length. Design-only placements
          // legitimately have no text line, hence the type check rather than a blanket one.
          const unresolved = this.allTextPlacements().filter((pl) => !this.textLineFor(pl));
          if (unresolved.length) {
            return this.handOverToWidget(
              'a text placement has no matching text line in options (' +
                unresolved.map((pl) => pl.text_line_key || '(no key)').join(', ') +
                ')'
            );
          }

          this.ready = true;
          this.watchVariantChanges();
          this.watchBundleChanges();
          this.guardAddToCart();
          // Re-read rather than trust the value from init: watchVariantChanges only registers
          // here, so a variant chosen while the config was in flight raised an event nobody was
          // listening for, and the server's first-paint answer is now stale.
          this.variantAvailable = this.selectedVariantAvailable();
          this.applyRequiredLayout();

          this.whenFontsWanted().then(() => this.ensureFonts());
          // Only reachable when the customer opened the modal while this was in flight.
          if (this.open) this.ensureFonts();
        } catch (e) {
          // Catch-all, and it earns its place: without it ANY unexpected throw below the fetch
          // (a missing geometry module, a bad ref, a geometry bug) left the component with
          // loading false, ready false and fallback false — the one state with no UI at all, so
          // the customer got an empty modal and no way to personalise. Every failure has to end
          // up at the legacy widget, including the ones we did not think of.
          this.handOverToWidget('unexpected error while preparing the preview (' + e.message + ')');
        } finally {
          this.loading = false;
        }
      },

      /**
       * Give up on the preview and let the legacy inline widget take the order instead.
       *
       * The preview is a nicety; the personalisation is the sale. Whatever brought us here, the
       * legacy widget can still take the SAME order, and not even from the same request: it tries
       * the LIVE PIM endpoint first with a 3s timeout and only falls back to this CDN, so a
       * CDN-only outage it does not notice. This used to hide the entry point instead, which
       * silently offered the customer no personalisation at all.
       *
       * One-way on purpose. Once the widget is mounted it owns the line-item properties and
       * the add-to-cart guard, and swapping back would leave two components claiming both.
       */
      handOverToWidget(reason) {
        console.warn('[preview] handing over to the inline widget: ' + reason);
        this.ready = false;
        this.fallback = true;
        if (this.open) this.closeModal();
        this.restoreThemeButtons();
      },

      /**
       * Re-render when the customer picks a different variant, so products whose variants
       * change appearance swap their base image.
       *
       * The theme broadcasts variant selection as section-scoped
       * `eurus:product-page-variant-select:updated:<sectionId>` / `...product-card-...`
       * events (see assets/variant-select.js). The page-level event carries only rendered
       * HTML, not the variant, so the id is re-read from the product form's `id` input —
       * which is the value actually submitted to the cart, and therefore the truth.
       */
      watchVariantChanges() {
        if (!this.sectionId) return;

        const reread = () => {
          const next = this.readVariantIdFromForm();
          if (next && next !== this.variantId) {
            this.variantId = next;
            this.applyVariantPricing();
            this.render();
          }

          // Unconditional, and outside the id check on purpose: the theme rebuilds the buy
          // buttons whenever it handles one of these events, so a required product gets its
          // second button back even when the variant id has not actually changed.
          this.variantAvailable = this.selectedVariantAvailable();
          this.applyRequiredLayout();
        };

        for (const name of [
          'eurus:product-page-variant-select:updated:' + this.sectionId,
          'eurus:product-card-variant-select:updated:' + this.sectionId,
        ]) {
          // Deferred a tick: the theme replaces form markup as part of handling these,
          // so the `id` input is only current after that work settles.
          document.addEventListener(name, () => setTimeout(reread, 0));
        }
      },

      /**
       * Read the selected variant from the product form's `id` input — the value actually
       * submitted to the cart, and therefore the truth.
       *
       * Scoped via productForms() rather than getElementById: the template renders two forms
       * sharing the same `product_form_id` (product-template.liquid 947 and 1225), so an
       * id lookup would only ever find the first.
       */
      readVariantIdFromForm() {
        for (const form of this.productForms()) {
          const input = form.querySelector('input[name="id"]');
          if (input && input.value) return String(input.value);
        }
        return null;
      },

      /** Keep the price summary in step with the selected variant. */
      applyVariantPricing() {
        const entry = this.variantPrices[String(this.variantId)];
        if (!entry) return;
        this.itemPriceCents = Number(entry.price) || 0;
        this.compareAtCents = Number(entry.compare_at) || 0;
      },

      /** Seed font/colour from the PIM defaults so the preview is never blank on open. */
      applyDefaults() {
        const stepFonts = {};
        const stepColours = {};
        let fontless = false;

        for (const step of this.allSteps()) {
          const fonts = this.fontChoices(step);
          const colours = this.colourChoices(step);
          stepFonts[step.key] = (fonts.find((f) => f.is_default) || fonts[0] || {}).value || null;
          stepColours[step.key] = (colours.find((c) => c.is_default) || colours[0] || {}).value || null;
          if (!stepFonts[step.key] && this.rawFontChoices(step).length) fontless = true;
        }

        // A group shot starts on its first line's component picks, so it draws what is ordered.
        for (const step of this.allSteps()) {
          if (this.stepComponentId(step) !== null) continue;
          const owner = this.stepTextLines(step).map((line) => this.lineStep(line)).find((s) => s && s.key !== step.key);
          if (!owner) continue;
          if (this.fontChoices(step).some((f) => f.value === stepFonts[owner.key])) stepFonts[step.key] = stepFonts[owner.key];
          if (this.colourChoices(step).some((c) => c.value === stepColours[owner.key])) stepColours[step.key] = stepColours[owner.key];
        }

        this.stepFonts = stepFonts;
        this.stepColours = stepColours;
        this.font = this.previewStep ? stepFonts[this.previewStep.key] || null : null;
        this.fontColour = this.previewStep ? stepColours[this.previewStep.key] || null : null;

        // A font whose webfont file is missing is filtered out of fontChoices(), so a product
        // whose ONLY font lacks a file leaves this null. Drawing anyway would fall back to the
        // browser's default serif and show the customer letterforms the workshop will never
        // stitch, so refuse the preview. The fix is to upload the file against that font in
        // Engine; until then the legacy widget still takes the order.
        // NOT gated on elementEnabled('font'). A step can hide the font picker while still being
        // stitched in a specific font, and that is common: single-font products hide the chooser.
        // Gating here meant those products drew the preview in the browser's default serif, which
        // is the one outcome this check exists to prevent, and they never handed over.
        if (fontless) {
          this.handOverToWidget('no usable font file for this product');
        }
      },

      // --- option accessors (config shape is the existing v1.0 `options` block) ---
      optionsBlock() {
        return (this.config && this.config.options) || {};
      },
      textLinesBlock() {
        return this.optionsBlock().text_lines || [];
      },
      /**
       * The text line a placement draws.
       *
       * A placement with NO `text_line_key` falls back to the first line, which is how configs
       * written before lines were keyed still work. A placement WITH a key that matches nothing
       * deliberately resolves to null instead of falling back: the fallback is what made the pink
       * bunny draw the same name on both ears, and a key that does not resolve is a config fault
       * worth handing over for, not one worth papering over.
       */
      textLineFor(placement) {
        if (!placement) return null;
        const lines = this.textLinesBlock();
        if (!placement.text_line_key) return lines[0] || null;
        return lines.find((l) => l.key === placement.text_line_key) || null;
      },
      textLineByKey(key) {
        return this.textLinesBlock().find((l) => l.key === key) || null;
      },

      /**
       * Field labels come from the config's TRANSLATIONS block, not from the text line.
       *
       * `options.text_lines[]` carries no `label` at all — it carries `label_key` (the unprefixed
       * key, e.g. `text_line_1`) and the human strings live under
       * `translations[locale][label_key]` as `label` / `placeholder` / `meta_text`, already
       * localised into every market. This was written reading a `line.label` that does not exist,
       * which showed BOTH of a two-field product's inputs as the theme's generic "Name" and wrote
       * the cart properties as "Name" and "Name 2" instead of "Name" and "Date".
       *
       * Resolution order is the legacy widget's, deliberately, so both UIs label a field
       * identically: exact locale, then any locale sharing its language, then the config's own
       * `base_locale`.
       */
      translationsBlock() {
        const all = (this.config && this.config.translations) || {};

        if (this.locale && all[this.locale]) return all[this.locale];

        if (this.locale) {
          const language = String(this.locale).split('-')[0].toLowerCase();
          const near = Object.keys(all).find((k) => k.toLowerCase().startsWith(language));
          if (near) return all[near];
        }

        const base = this.config && this.config.base_locale;
        return (base && all[base]) || {};
      },

      /** `label` / `placeholder` / `meta_text` for one line, or an empty object. */
      lineTranslation(line) {
        if (!line) return {};
        return this.translationsBlock()[line.label_key || line.key] || {};
      },

      /**
       * The name this field goes into the cart under, and what the workshop reads.
       *
       * Falls back to `label_key` rather than to a theme string, matching the legacy widget: an
       * order property has to be stable and recognisable, and `text_line_2` is at least both.
       */
      lineLabel(line) {
        // Deliberately NOT falling back to `line.key`: that is the technical property name, so the
        // pair would be written twice under one name and the human-readable one would be lost.
        return this.lineTranslation(line).label || (line && line.label_key) || 'Name';
      },
      textPlacementsOf(step) {
        const out = [];
        for (const view of this.geometry.resolveViews(step)) {
          for (const placement of (view && view.placements) || []) {
            if (!placement || (placement.type && placement.type !== 'text')) continue;
            out.push(placement);
          }
        }
        return out;
      },

      allTextPlacements() {
        const out = [];
        for (const step of this.allSteps()) out.push(...this.textPlacementsOf(step));
        return out;
      },

      stepTextLines(step) {
        const out = [];
        const seen = new Set();
        for (const placement of this.textPlacementsOf(step)) {
          const line = this.textLineFor(placement);
          // A line with no key cannot be stored against, keyed or written as a property.
          if (!line || !line.key || seen.has(line.key)) continue;
          if (!this.lineInBundle(line)) continue;
          seen.add(line.key);
          out.push(line);
        }
        return out;
      },

      /** A line routed only to unchosen or out-of-stock components does not apply. */
      lineInBundle(line) {
        const ids = Array.isArray(line && line.bundle_component_ids) ? line.bundle_component_ids.map(Number) : [];
        if (!ids.length || !this.bundleState) return true;
        const selected = this.bundleState.selectedIds || [];
        const oos = this.bundleState.oosIds || [];
        return ids.some((id) => selected.indexOf(id) !== -1 && oos.indexOf(id) === -1);
      },

      currentTextLines() {
        return this.stepTextLines(this.previewStep);
      },

      /**
       * Every text line THE PRODUCT asks the customer for, in view then placement order, deduped.
       *
       * ACROSS ALL VIEWS, not just the one on screen, and that is the whole point: the name, font
       * and colour are properties of the ORDER — every view draws the same values in its own
       * position (see selectView). Deriving the FIELDS from the active view instead put two real
       * bugs in reach on a product whose second line lives on another view: the customer was shown
       * no input for it, so a required line could never be satisfied, and `applyToForms` wrote only
       * the visible view's properties, so switching view dropped the other line from the cart.
       *
       * Placement order within a view, because the merchandiser positioned them and that is the
       * order they read on the product: the left ear before the right one. Deduped because two views
       * commonly draw the SAME line (a name on the front and again on the strap) and that is one
       * field, not two.
       */
      textLines() {
        // Every step in the order, not just the one on screen.
        const out = [];
        const seen = new Set();
        for (const step of this.activeSteps()) {
          for (const line of this.stepTextLines(step)) {
            if (seen.has(line.key)) continue;
            seen.add(line.key);
            out.push(line);
          }
        }
        return out;
      },
      hasMultipleTextLines() {
        return this.textLines().length > 1;
      },
      /**
       * The line the legacy single-value state belongs to.
       *
       * `text` predates `textValues` and is still what pricing, the add-to-cart guard and a
       * placement with no `text_line_key` read, so one line has to own it.
       *
       * THE FIRST LINE THE PRODUCT ASKS FOR, which is now stable because `textLines()` spans every
       * view rather than the one on screen. It was briefly the first field of the ACTIVE VIEW, and
       * that made `text` mean a different line depending on which view was open: the name typed for
       * the front read back as the strap's value after a switch.
       *
       * NOT simply the config's first line, which was the other candidate and is wrong in a way
       * staging already contains: `pm=10` has one placement and it points at `text_line_2`, with
       * `text_line_1` present in the options and drawn nowhere. Taking the config's first line there
       * would price the order off a field the customer is never shown, and leave `text` holding a
       * value nothing draws. For every single-line product all three definitions are the same line.
       */
      primaryLine() {
        return this.textLines()[0] || this.textLinesBlock()[0] || null;
      },
      /** Kept under its old name because most of the file, and every single-line product, uses it. */
      textLine() {
        return this.primaryLine();
      },
      /** Value for one line, defaulting through `text` so the primary line has a single source. */
      textFor(key) {
        // A bundle's first line changes with the selection, so bundle values are kept by line key.
        const primary = this.isBundle() ? null : this.primaryLine();
        if (primary && key === primary.key) return this.text || '';
        return this.textValues[key] || '';
      },
      rawFontChoices(step = this.previewStep) {
        if (step && Array.isArray(step.fonts)) return step.fonts;
        return this.optionsBlock().fonts || [];
      },
      fontChoices(step = this.previewStep) {
        // Only fonts that actually ship a webfont file, and only ones that then LOADED. The
        // second half matters: a file can be recorded in the config and still 404, and drawing in
        // a family that was never registered silently falls back to the browser's default serif.
        return this.rawFontChoices(step).filter(
          (f) => f && f.font_file_url && this.unloadableFonts.indexOf(f.value) === -1
        );
      },
      colourChoices(step = this.previewStep) {
        if (step && Array.isArray(step.font_colours)) return step.font_colours;
        return this.optionsBlock().font_colours || [];
      },
      uppercaseOnly(key) {
        const tl = key === undefined ? this.textLine() : this.textLineByKey(key);
        return !!tl && tl.uppercase_only === true;
      },
      charLimit(key) {
        const tl = key === undefined ? this.textLine() : this.textLineByKey(key);
        return (tl && tl.char_limit) || null;
      },
      /**
       * Character limits keyed by line, for the geometry.
       *
       * They are what settles the DRAWN SIZE now, not just what the input accepts: the geometry
       * picks a size at which a limit-length name fits and then never changes it, so the preview
       * no longer resizes while the customer types. Stop passing this and the old per-keystroke
       * shrink comes back — see `constantEmSize` in the geometry module.
       */
      charLimits() {
        return this.geometry.charLimitsFrom(this.optionsBlock());
      },
      /** Every view this step offers, in the merchandiser's order. One entry for an older config. */
      views() {
        return this.geometry.resolveViews(this.previewStep);
      },

      /** The view being drawn. */
      activeView() {
        return this.geometry.resolveView(this.previewStep, this.activeViewKey);
      },

      hasMultipleViews() {
        return this.views().length > 1;
      },

      /** Merchandiser's choice per step; configs that predate the toggle keep the step heading. */
      showViewLabels() {
        return !this.previewStep || this.previewStep.show_view_labels !== false;
      },

      // ---------------------------------------------------------------------------
      // Bundle steps (MFYEB-186)
      // ---------------------------------------------------------------------------

      allSteps() {
        const steps = (this.preview && Array.isArray(this.preview.steps)) ? this.preview.steps : [];
        return steps
          .filter((s) => s && s.key && this.geometry.resolveViews(s).length)
          .slice()
          .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0));
      },

      isBundle() {
        return this.allSteps().some((s) => Object.prototype.hasOwnProperty.call(s, 'bundle_component_id'));
      },

      stepComponentId(step) {
        return step && step.bundle_component_id != null ? Number(step.bundle_component_id) : null;
      },

      /** Unchosen components are not in the order; every step counts until the widget answers. */
      stepSelected(step) {
        const id = this.stepComponentId(step);
        if (id === null || !this.bundleState || !Array.isArray(this.bundleState.selectedIds)) return true;
        return this.bundleState.selectedIds.indexOf(id) !== -1;
      },

      /** Out-of-stock steps are shown and marked but not personalised (clarification 4). */
      stepOutOfStock(step) {
        const id = this.stepComponentId(step);
        if (id === null || !this.bundleState || !Array.isArray(this.bundleState.oosIds)) return false;
        return this.bundleState.oosIds.indexOf(id) !== -1;
      },

      /** The steps in the stepper: every one the bundle contains, out of stock ones included. */
      steps() {
        return this.allSteps().filter((s) => this.stepSelected(s));
      },

      activeSteps() {
        return this.steps().filter((s) => !this.stepOutOfStock(s));
      },

      hasMultipleSteps() {
        return this.steps().length > 1;
      },

      syncActiveStep() {
        if (!this.preview) return;
        const steps = this.steps();
        const usable = this.activeSteps();
        const current = steps.find((s) => s.key === this.activeStepKey);
        const next = current || usable[0] || steps[0] || this.geometry.resolveStep(this.preview, null);
        if (!next) return;

        if (!this.previewStep || this.previewStep.key !== next.key) {
          this.previewStep = next;
          this.activeViewKey = null;
          this._collapseUnstable = false;
        }
        this.activeStepKey = next.key;
        // Only once the defaults are seeded: before that the maps are empty and `font` is too.
        if (Object.prototype.hasOwnProperty.call(this.stepFonts, next.key)) {
          this.font = this.stepFonts[next.key];
          this.fontColour = this.stepColours[next.key];
        }
      },

      selectStep(key) {
        const step = this.steps().find((s) => s.key === key);
        if (!step || this.stepOutOfStock(step)) return;
        this.activeStepKey = key;
        this.syncActiveStep();
        this.render();
        this.focusNameInput();
        this.revealActiveStepTab();
      },

      stepIndex(step = this.previewStep) {
        return this.steps().findIndex((s) => step && s.key === step.key);
      },

      nextStepAfter(step = this.previewStep) {
        const steps = this.steps();
        for (let i = this.stepIndex(step) + 1; i < steps.length; i++) {
          if (!this.stepOutOfStock(steps[i])) return steps[i];
        }
        return null;
      },

      isLastStep() {
        return !this.nextStepAfter();
      },

      nextStep() {
        if (!this.validateLines(this.currentTextLines())) return;
        const next = this.nextStepAfter();
        if (next) {
          this.selectStep(next.key);
          this.scrollPaneToTop();
        }
      },

      previousStep() {
        const steps = this.steps();
        for (let i = this.stepIndex() - 1; i >= 0; i--) {
          if (!this.stepOutOfStock(steps[i])) {
            this.selectStep(steps[i].key);
            this.scrollPaneToTop();
            return;
          }
        }
      },

      isLongLine(line) {
        return this.hasMultipleSteps() && Number(line && line.char_limit) > 50;
      },

      /** Required lines filled and something typed, so an all-optional step is not shown as done. */
      stepComplete(step) {
        const lines = this.stepTextLines(step);
        const filled = (line) => !!this.textFor(line.key).trim();
        return lines.length > 0 && lines.every((line) => !line.required || filled(line)) && lines.some(filled);
      },

      stepHasError(step) {
        return this.stepTextLines(step).some((line) => this.validationErrorFor(line.key));
      },

      /** Active view's name, falling back to the step name for unnamed views. */
      stepHeading() {
        const view = this.activeView();
        const label = view && String(view.label || '').trim();
        if (label && label !== view.key && view.key !== 'default') return label;
        return this.stepLabel(this.previewStep);
      },

      stepLabel(step) {
        if (!step) return '';
        return step.label || (step.component && step.component.title) || '';
      },

      /** The step of the component the line is routed to, else the first step drawing it. */
      lineStep(line) {
        if (!line) return null;
        const steps = this.activeSteps();
        const routed = Array.isArray(line.bundle_component_ids) ? line.bundle_component_ids.map(Number) : [];
        const drawing = steps.filter((s) => this.stepTextLines(s).some((l) => l.key === line.key));
        return (
          drawing.find((s) => routed.indexOf(this.stepComponentId(s)) !== -1) ||
          drawing.find((s) => this.stepComponentId(s) !== null) ||
          drawing[0] ||
          null
        );
      },

      lineFont(line) {
        const step = this.lineStep(line);
        return step ? this.stepFonts[step.key] || null : this.font;
      },

      lineColour(line) {
        const step = this.lineStep(line);
        return step ? this.stepColours[step.key] || null : this.fontColour;
      },

      bundleRoot() {
        if (!this.productId || typeof document === 'undefined' || !document.querySelector) return null;
        return document.querySelector('#engine-bundle-container[data-product-id="' + this.productId + '"]');
      },

      readBundleState() {
        if (!this.isBundle()) return;
        const root = this.bundleRoot();
        const data = root && window.Alpine && typeof Alpine.$data === 'function' ? Alpine.$data(root) : null;
        if (data && typeof data.bundleState === 'function') this.applyBundleState(data.bundleState());
      },

      applyBundleState(state) {
        if (!state) return;
        this.bundleState = {
          selectedIds: (state.selectedIds || []).map(Number),
          oosIds: (state.oosIds || []).map(Number),
          valid: state.valid !== false,
          bundlePrice: state.bundlePrice,
        };
        if (this.bundleState.valid) this.bundleOptionsError = false;
        if (typeof state.bundlePrice === 'number' && isFinite(state.bundlePrice)) {
          this.itemPriceCents = Math.round(state.bundlePrice * 100);
        }
      },

      watchBundleChanges() {
        if (!this.isBundle() || this._watchingBundle) return;
        this._watchingBundle = true;
        document.addEventListener('engine-bundle:updated', (event) => {
          if (this.fallback) return;
          const detail = (event && event.detail) || {};
          if (String(detail.productId) !== String(this.productId)) return;
          this.applyBundleState(detail);
          this.syncActiveStep();
          this.render();
          this.applyToForms();
          this.variantAvailable = this.selectedVariantAvailable();
        });
        // Updates sent while the fonts loaded were missed, so take the current state now.
        this.readBundleState();
        this.syncActiveStep();
        this.render();
        this.applyToForms();
      },

      stepTabsScroll: { left: false, right: false },

      watchStepTabs(row) {
        this.updateStepTabs();
        if (typeof ResizeObserver === 'undefined' || !row) return;
        new ResizeObserver(() => this.updateStepTabs()).observe(row);
      },

      updateStepTabs() {
        const row = this.$refs && this.$refs.stepTabs;
        if (!row) return;
        const max = row.scrollWidth - row.clientWidth;
        this.stepTabsScroll = { left: row.scrollLeft > 2, right: row.scrollLeft < max - 2 };
      },

      onStepTabsWheel(event) {
        const row = this.$refs && this.$refs.stepTabs;
        if (!row || row.scrollWidth <= row.clientWidth || !event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        event.preventDefault();
        row.scrollLeft += event.deltaY;
      },

      scrollStepTabs(direction) {
        const row = this.$refs && this.$refs.stepTabs;
        if (row) row.scrollBy({ left: direction * row.clientWidth * 0.6, behavior: 'smooth' });
      },

      revealActiveStepTab() {
        this.$nextTick(() => {
          const row = this.$refs && this.$refs.stepTabs;
          const key = this.previewStep && this.previewStep.key;
          const tab = row && key && Array.from(row.querySelectorAll('[data-step-tab]')).find((t) => t.dataset.stepTab === key);
          if (tab && tab.scrollIntoView) tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
          this.updateStepTabs();
        });
      },

      scrollPaneToTop() {
        this.$nextTick(() => {
          const root = this.$el;
          const pane = root && root.querySelector ? root.querySelector('[data-personalisation-pane]') : null;
          if (pane) pane.scrollTop = 0;
        });
      },

      /**
       * Switch view.
       *
       * Nothing about the personalisation changes: the name, font and colour are properties of the
       * ORDER, and every view draws the same values in its own position. So this only changes what
       * is being looked at, and the placement geometry comes from the view itself.
       */
      selectView(viewKey) {
        if (viewKey === this.activeViewKey) return;

        this.activeViewKey = viewKey;

        // A different view means a different base photo and so a different stage height, which is
        // what decided the collapse was unstable. Give it a fresh chance. See onPaneScroll().
        this._collapseUnstable = false;

        this.render();
      },

      /** Every placement on the ACTIVE VIEW, in config order. */
      placements() {
        const view = this.activeView();

        return (view && Array.isArray(view.placements)) ? view.placements : [];
      },
      firstPlacement() {
        return this.placements()[0] || null;
      },
      /** Placements that draw a design rather than text. */
      designPlacements() {
        return this.placements().filter((p) => p && p.type === 'design');
      },
      /**
       * Choices for a design placement's select option.
       *
       * Shape: `options.designs` is a map of option_key -> [{ value, label, image_url, ... }].
       * Read defensively because the Engine design catalogue does not exist yet — a product
       * with a design placement but no catalogue simply renders no overlay, which is the same
       * outcome as the customer not having chosen one.
       */
      designChoices(optionKey) {
        const all = this.optionsBlock().designs || {};
        const list = all[optionKey];
        return Array.isArray(list) ? list.filter((c) => c && c.image_url) : [];
      },
      /** option_key -> chosen artwork URL, for the geometry to position. */
      designUrls() {
        const out = {};
        for (const placement of this.designPlacements()) {
          const key = placement.option_key;
          if (!key) continue;
          const chosen = this.designSelections[key];
          if (!chosen) continue;
          const match = this.designChoices(key).find((c) => c.value === chosen);
          if (match) out[key] = match.image_url;
        }
        return out;
      },
      /**
       * Is a given control shown for the active step?
       *
       * `elements` lives on the STEP, not the preview root. Engine's Customization Builder
       * selects "fields shown on this image" per step, so a card step can take text without
       * offering a font choice. Falls back to the preview root for forward/backward safety if
       * a config is produced by an older exporter.
       */
      elementEnabled(name) {
        return this.stepElementEnabled(this.previewStep, name);
      },
      stepElementEnabled(step, name) {
        const stepEls = (step && step.elements) || null;
        if (stepEls && Object.prototype.hasOwnProperty.call(stepEls, name)) {
          return stepEls[name] !== false;
        }
        const rootEls = (this.preview && this.preview.elements) || {};
        return rootEls[name] !== false;
      },

      /** True once we have enough to draw anything at all. */
      get renderable() {
        return !!(this.previewStep && this.firstPlacement() && this.baseImage());
      },

      baseImage() {
        // From the ACTIVE VIEW. Passing the step drew the primary view's photograph whichever view
        // was selected, so the name would be positioned against a picture of something else.
        return this.geometry.resolveBaseImage(this.activeView(), this.variantId);
      },

      /**
       * Colour to draw the name in, resolved by the shared module so this page and the cart line
       * preview cannot disagree. The fallback order and the reasons for it live with the function.
       */
      selectedColourHex() {
        const step = this.previewStep;
        const stepDefault = step && step.default_colour_hex;
        return this.geometry.resolveFill(
          this.colourChoices(),
          this.fontColour,
          stepDefault || (this.preview && this.preview.default_colour_hex)
        );
      },

      previewMethod() {
        const step = this.previewStep;
        return (step && step.method) || (this.preview && this.preview.method) || undefined;
      },

      /** A group-shot placement draws in its owning component's font, colour and method; null keeps the active step's. */
      placementStyle(placement) {
        const line = this.textLineFor(placement);
        const owner = line && this.lineStep(line);
        const groupShot = this.previewStep && this.stepComponentId(this.previewStep) === null;
        if (!groupShot || !owner || this.stepComponentId(owner) === null || owner.key === this.previewStep.key) return null;

        const code = this.stepFonts[owner.key] || this.font;
        const family = this.fontFamilyFor(code);
        return {
          fontFamily: family,
          fill: this.geometry.resolveFill(
            this.colourChoices(owner),
            this.stepColours[owner.key],
            owner.default_colour_hex || (this.preview && this.preview.default_colour_hex)
          ),
          method: owner.method || this.previewMethod(),
          measure: (t, size) => this.measureText(t, size, family),
          measureCapHeight: (size) => this.measureCapHeight(size, family),
          measureInkHeight: (size) => this.measureInkHeight(size, family),
        };
      },

      /**
       * Human-readable labels for the cart line properties (falling back to the raw code).
       *
       * The workshop reads these, not the technical `_font` / `_font_colour` values, so they
       * have to resolve to the merchandiser's label wherever they are written from.
       */
      selectedFontLabel() {
        const f = this.fontChoices().find((x) => x.value === this.font);
        return (f && f.label) || this.font || '';
      },
      selectedColourLabel() {
        const c = this.colourChoices().find((x) => x.value === this.fontColour);
        return (c && c.label) || this.fontColour || '';
      },

      /**
       * Family naming, measurement and font loading all come from PimConfig, so this component, the
       * cart line preview and the geometry contract cannot drift apart. The family name in
       * particular must match the widget's namespacing exactly — see the file header.
       */
      fontFamilyFor(code) {
        return window.PimConfig.fontFamilyFor(code);
      },

      whenFontsWanted() {
        return window.PimConfig.whenFontsWanted(this.$el);
      },

      /**
       * Load the fonts once, after the config, and redraw in them. Not part of loadConfig, so the
       * add-to-cart guard does not wait on font files the shopper may never look at (MFS-639).
       */
      ensureFonts() {
        if (!this._fontsPromise) {
          this._fontsPromise = this.ensureConfig()
            .then(() => (this.ready ? this.loadFonts() : undefined))
            .then(() => {
              if (!this.ready) return;
              this.render();
              if (this.open) this.focusNameInput();
            })
            .catch((e) => this.handOverToWidget('unexpected error while loading fonts (' + e.message + ')'))
            .finally(() => {
              if (!this.fontsReady) this._fontsPromise = null;
            });
        }
        return this._fontsPromise;
      },

      /**
       * Register every previewable font as a FontFace.
       *
       * The cache lives in PimConfig now, keyed by family and shared with the cart line preview, so
       * a face loaded here is not loaded a second time down there. Each component used to keep its
       * own cache, which meant exactly that duplicate work for a customer who reached the cart.
       *
       * A font that fails resolves false rather than throwing, so one missing file cannot stop the
       * others. Whether the product can be previewed at all is decided in seedSelections, which
       * hands over to the legacy widget when there is no usable font.
       */
      async loadFonts() {
        const choices = [];
        const seenFonts = new Set();
        for (const step of this.allSteps()) {
          for (const choice of this.fontChoices(step)) {
            if (seenFonts.has(choice.value)) continue;
            seenFonts.add(choice.value);
            choices.push(choice);
          }
        }
        if (!choices.length) {
          this.fontsReady = true;
          return;
        }

        // THE RESULTS ARE READ, not discarded. registerFont resolves false for a file that 404s
        // or times out, and this used to throw that away and set fontsReady regardless, so the
        // customer saw their name in the browser's default serif: letterforms the workshop will
        // never stitch. seedSelections already refuses when a font has NO file recorded; a file
        // recorded but unfetchable went straight through, which was the same fault by a different
        // route. The cart line preview has always refused on this, so the two now agree.
        const results = await Promise.all(
          choices.map((choice) =>
            window.PimConfig.registerFont(choice).then((ok) => ({ choice: choice, ok: ok }))
          )
        );

        for (const r of results) {
          if (!r.ok && r.choice && r.choice.value) this.unloadableFonts.push(r.choice.value);
        }

        this.fontsReady = true;

        // fontChoices() now excludes whatever failed, so re-settle every step against what is left.
        const stepFonts = Object.assign({}, this.stepFonts);
        for (const step of this.allSteps()) {
          const usable = this.fontChoices(step);

          if (!usable.length) {
            if (this.rawFontChoices(step).length) {
              return this.handOverToWidget('no font file would load for this product');
            }
            continue;
          }

          // The chosen font was one of the casualties. Move to one that works rather than refusing
          // outright: unlike a cart line, the product page has other typefaces it can legitimately
          // offer, and the customer has not committed to anything yet. Preferring the product's
          // default keeps it as close to the intended look as the data allows.
          if (!usable.some((f) => f.value === stepFonts[step.key])) {
            stepFonts[step.key] = (usable.find((f) => f.is_default) || usable[0]).value;
          }
        }
        this.stepFonts = stepFonts;
        if (this.previewStep && Object.prototype.hasOwnProperty.call(stepFonts, this.previewStep.key)) {
          this.font = stepFonts[this.previewStep.key];
        }
      },

      /**
       * Text measurement, in SVG user units, via PimConfig's shared canvas.
       *
       * The geometry module asks its caller for these rather than measuring anything itself, which
       * is what lets it be unit-tested in node and reused by Engine's admin editor. Canvas
       * measurement resolves against fonts registered in `document.fonts`, so it works for the PIM
       * webfonts and avoids a render-measure-rerender round trip.
       *
       * Family is passed explicitly rather than read from state: measuring against a font other
       * than the selected one is a legitimate need, and an implicit dependency would silently
       * measure the wrong face.
       */
      measureText(text, fontSizeUserUnits, family) {
        return window.PimConfig.measureWidth(text, fontSizeUserUnits, family);
      },

      /** Ink height of a capital X, which is what makes two fonts render at the same visual size. */
      measureCapHeight(fontSizeUserUnits, family) {
        return window.PimConfig.measureCapHeight(fontSizeUserUnits, family);
      },

      /** How far a row of ordinary text reaches above and below its baseline: the row pitch of a message. */
      measureInkHeight(fontSizeUserUnits, family) {
        return window.PimConfig.measureInkHeight(fontSizeUserUnits, family);
      },

      /**
       * Recompute and apply one frame. Runs on every keystroke.
       *
       * Attributes are written onto persistent SVG nodes rather than re-setting innerHTML,
       * so the <image> element is never recreated — recreating it would re-resolve the href
       * on every character typed and can flash on slow connections.
       */
      render() {
        if (!this.renderable || !this.fontsReady) return;

        const texts = this.allTexts();
        const primary = this.primaryLine();
        const styles = new Map();
        const result = this.geometry.computeRender({
          image: this.baseImage(),
          // Every placement on the step, not just the first: a step can hold a name, a second
          // line and an embroidery design.
          placements: this.placements(),
          // Via allTexts so an uppercase-only primary line draws capitals mid-composition too.
          text: (primary && texts[primary.key]) || this.text,
          texts,
          // What makes the drawn size independent of how much has been typed.
          charLimits: this.charLimits(),
          designUrls: this.designUrls(),
          fontFamily: this.fontFamilyFor(this.font),
          fill: this.selectedColourHex(),
          collapsed: this.collapsed,
          focus: this.focusPoint(),
          collapsedAspect: this.collapsedAspect(),
          measure: (t, size) => this.measureText(t, size, this.fontFamilyFor(this.font)),
          // Keeps the name the same visual height when the customer switches font.
          measureCapHeight: (size) => this.measureCapHeight(size, this.fontFamilyFor(this.font)),
          // Row pitch of a multi-line message, so its spacing follows the visible letters.
          measureInkHeight: (size) => this.measureInkHeight(size, this.fontFamilyFor(this.font)),
          // How the product is decorated (embroidery / flock / print / deboss), which changes the
          // letterform's weight and how much lighter than its recorded colour it is drawn. Engine
          // derives it from the product's routing, so this preview and the factory agree.
          method: this.previewMethod(),
          styleFor: (placement) => {
            if (!styles.has(placement)) styles.set(placement, this.placementStyle(placement));
            return styles.get(placement);
          },
        });

        /*
         * `result.fit.clamped` and `result.fit.fits` ARE DELIBERATELY NOT SURFACED TO THE CUSTOMER.
         *
         * There used to be an amber note here, "This name is longer than the embroidery area
         * comfortably fits.", raised on `clamped || fits === false`. It was removed because it
         * reports a merchandising problem to the wrong audience: the customer cannot act on it,
         * their name is within the character limit they were given, and the box is a preview
         * drawing region rather than a machine constraint, so the warning implied a factory limit
         * that does not exist. The name is still drawn, shrunk to the MIN_SHRINK_RATIO floor.
         *
         * The signal itself is still computed by the geometry module and is worth having: `clamped`
         * means the config could not be drawn as configured, which is a real thing to fix in the
         * PIM (usually a `char_limit` too generous for the box, or a stale `font_size`). If it
         * needs surfacing again, put it somewhere the merchandiser looks, not on the storefront.
         */
        this.applyToSvg(result);
      },

      applyToSvg(r) {
        const svg = this.$refs.svg;
        if (!svg) return;

        // viewBox is written imperatively because HTML lowercases attribute names, so an
        // Alpine `:viewBox` binding would emit the invalid `viewbox` for SVG.
        this._stageImage = r.image;
        svg.setAttribute('viewBox', this.liveViewBox() || r.viewBox);
        svg.setAttribute('aria-label', r.ariaLabel);
        this.watchStageSize();

        const image = this.$refs.baseImage;
        if (image) {
          // Only touch href when it actually changed, to avoid any refetch.
          if (image.getAttribute('href') !== r.image.url) {
            image.setAttribute('href', r.image.url);
          }
          image.setAttribute('width', r.image.width);
          image.setAttribute('height', r.image.height);
        }

        this.applyLayers(r);
      },

      /** Crop for the stage's current size, so the crop follows the collapse animation. */
      liveViewBox() {
        const svg = this.$refs && this.$refs.svg;
        const image = this._stageImage;
        const stage = svg && svg.parentElement;
        if (!stage || !image || !(image.width > 0) || !(image.height > 0)) return null;
        const box = stage.getBoundingClientRect();
        if (!(box.width > 0) || !(box.height > 0)) return null;
        const vb = this.geometry.collapsedViewBox(image.width, image.height, this.focusPoint(), box.width / box.height);
        return [vb.x, vb.y, vb.width, vb.height].map((n) => Math.round(n * 100) / 100).join(' ');
      },

      watchStageSize() {
        if (this._stageObserver || typeof ResizeObserver === 'undefined') return;
        const svg = this.$refs && this.$refs.svg;
        if (!svg || !svg.parentElement) return;
        this._stageObserver = new ResizeObserver(() => {
          const viewBox = this.liveViewBox();
          if (viewBox) svg.setAttribute('viewBox', viewBox);
        });
        this._stageObserver.observe(svg.parentElement);
      },

      /**
       * Draw every layer the geometry produced, reusing nodes across renders.
       *
       * A step can hold more than one placement (a name and a date, or a name and an
       * embroidery design), so this manages a small pool of <text>/<image> nodes rather than
       * one static element. Nodes are created once per slot and then only have attributes
       * rewritten, the same rule the base image follows: nothing that the browser would have to
       * refetch or re-layout gets recreated on a keystroke.
       *
       * Values always go in through textContent, never innerHTML, so a name cannot inject
       * markup.
       */
      applyLayers(r) {
        const root = this.$refs.layerRoot;
        if (!root) return;

        const NS = 'http://www.w3.org/2000/svg';
        this._layerNodes = this._layerNodes || [];
        const layers = (r && r.layers) || [];

        layers.forEach((layer, i) => {
          const wanted = layer.kind === 'design' ? 'image' : 'text';
          let node = this._layerNodes[i];

          // A slot can change kind — a step could gain a design where a name used to be — so
          // the tag is checked, not just the presence of a node.
          if (!node || node.tagName !== wanted) {
            if (node && node.parentNode) node.parentNode.removeChild(node);
            node = document.createElementNS(NS, wanted);
            root.appendChild(node);
            this._layerNodes[i] = node;
          }

          if (layer.kind === 'design') this.applyDesignLayer(node, layer);
          else this.applyTextLayer(node, layer, i);
        });

        // Drop surplus nodes rather than hiding them, so a removed placement cannot leave a
        // stale name on the garment.
        for (let i = layers.length; i < this._layerNodes.length; i++) {
          const node = this._layerNodes[i];
          if (node && node.parentNode) node.parentNode.removeChild(node);
        }
        this._layerNodes.length = layers.length;
      },

      /** A transparent PNG (an embroidery design) sitting in the placement box. */
      applyDesignLayer(node, layer) {
        if (node.getAttribute('href') !== layer.url) {
          node.setAttribute('href', layer.url);
          // xlink for older engines; both point at the same asset so it is harmless.
          node.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', layer.url);
        }
        node.setAttribute('x', layer.x);
        node.setAttribute('y', layer.y);
        node.setAttribute('width', layer.width);
        node.setAttribute('height', layer.height);
        // Fits the artwork inside the box and centres the remainder, so the contract never has
        // to carry each design's pixel size.
        node.setAttribute('preserveAspectRatio', layer.preserveAspectRatio);
        if (layer.transform) node.setAttribute('transform', layer.transform);
        else node.removeAttribute('transform');
        node.setAttribute('visibility', 'visible');
      },

      /** One line of personalised text, straight or on a curved baseline. */
      applyTextLayer(node, layer, index) {
        node.setAttribute('visibility', 'visible');

        // On a curved baseline x/y have to come off: they fight the path, and engines disagree
        // about which wins.
        if (layer.arc) {
          node.removeAttribute('x');
          node.removeAttribute('y');
        } else {
          node.setAttribute('x', layer.x);
          node.setAttribute('y', layer.y);
        }
        node.setAttribute('text-anchor', layer.textAnchor);
        node.setAttribute('dominant-baseline', layer.dominantBaseline);
        node.setAttribute('text-rendering', layer.textRendering);
        node.setAttribute('font-family', layer.fontFamily);
        node.setAttribute('font-size', layer.fontSize);
        node.setAttribute('fill', layer.fill);

        // From the layer, not hardcoded: it was a literal here once, so the editor, the cart and
        // the offline render all drew at full strength while the customer saw 90%. See
        // METHOD_TREATMENTS in the geometry module, where it is set per decoration method.
        node.setAttribute('opacity', layer.opacity);

        // Satin stitch is wider than the outline the font describes, and its stroke ends are
        // rounded. Stroking in the SAME colour underneath the fill reproduces both without
        // touching the letterform or the colour. `paint-order` is what puts the stroke beneath;
        // without it the stroke paints over the fill and dulls it.
        if (layer.strokeWidth) {
          node.setAttribute('stroke', layer.fill);
          node.setAttribute('stroke-width', layer.strokeWidth);
          node.setAttribute('stroke-linejoin', 'round');
          node.setAttribute('stroke-linecap', 'round');
          node.setAttribute('paint-order', 'stroke fill');
        } else {
          node.removeAttribute('stroke');
          node.removeAttribute('stroke-width');
          node.removeAttribute('paint-order');
        }
        if (layer.letterSpacing) node.setAttribute('letter-spacing', layer.letterSpacing);
        else node.removeAttribute('letter-spacing');
        if (layer.transform) node.setAttribute('transform', layer.transform);
        else node.removeAttribute('transform');

        this.applyArc(node, layer, index);
      },

      /**
       * Put a line on a curved baseline, or back on a straight one.
       *
       * Each line owns its own <path> in <defs>, keyed by layer index, so two curved lines in
       * one step cannot end up following the same curve.
       */
      applyArc(node, layer, index) {
        const defs = this.$refs.arcDefs;
        this._arcPaths = this._arcPaths || [];
        this._arcTextPaths = this._arcTextPaths || [];

        if (!layer.arc || !defs) {
          const existing = this._arcTextPaths[index];
          if (existing && existing.parentNode === node) node.removeChild(existing);
          this.applyLines(node, layer);
          return;
        }

        const NS = 'http://www.w3.org/2000/svg';
        const id = (this.arcPathIdBase || 'pim-arc') + '-' + index;

        let path = this._arcPaths[index];
        if (!path) {
          path = document.createElementNS(NS, 'path');
          path.setAttribute('id', id);
          path.setAttribute('fill', 'none');
          defs.appendChild(path);
          this._arcPaths[index] = path;
        }
        path.setAttribute('d', layer.arc.d);

        let textPath = this._arcTextPaths[index];
        if (!textPath) {
          textPath = document.createElementNS(NS, 'textPath');
          // `href` is SVG2 and what modern browsers use; `xlink:href` keeps older engines
          // working. Both point at the same node.
          textPath.setAttribute('href', '#' + id);
          textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#' + id);
          this._arcTextPaths[index] = textPath;
        }
        textPath.setAttribute('startOffset', layer.arcStartOffset || '50%');
        textPath.textContent = layer.value;

        if (textPath.parentNode !== node) {
          node.textContent = '';
          node.appendChild(textPath);
        }
      },

      /** SVG text ignores line breaks, so each line of a message gets its own positioned <tspan>. */
      applyLines(node, layer) {
        const lines = layer.lines || [];
        if (lines.length < 2) {
          node.textContent = layer.value;
          return;
        }

        node.textContent = '';
        lines.forEach((line) => {
          const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          tspan.setAttribute('x', layer.x);
          tspan.setAttribute('y', line.y);
          tspan.textContent = line.value;
          node.appendChild(tspan);
        });
      },

      focusPoint() {
        const explicit = this.previewStep && this.previewStep.focus;
        if (explicit && typeof explicit.y === 'number') return explicit;
        return this.geometry.defaultFocus(this.previewStep);
      },

      /**
       * Aspect ratio of the collapsed (scrolled) strip, from the mobile design.
       *
       * Deliberately a constant rather than measured from the container: `stageStyle` sets
       * the container's aspect-ratio from this value, so measuring it back would be circular.
       */
      collapsedAspect() {
        return 390 / 150;
      },

      /**
       * Reserve the stage's aspect ratio up front so the image landing doesn't shift layout.
       * Expanded uses the base image's natural ratio; collapsed uses the design's band.
       *
       * NO CSS TRANSFORM HERE ON PURPOSE. Rotation and the collapsed crop are done inside the SVG
       * (a `transform` attribute on the text, and a viewBox crop), so everything stays vector and
       * is rasterised at device resolution. If a zoom or a CSS transform is ever added to this
       * container, be aware of a gotcha the reference engraving project hit and documented: with
       * the transform set to `none` at rest, the browser sampled their counter-rotated preview
       * image through a low-quality path and it rendered blurry, and their fix was a near-identity
       * `scale(1.001)` to force a device-resolution rasterisation of the subtree. It does not apply
       * while this stays transform-free.
       */
      get stageStyle() {
        if (this.collapsed && this.isMobileLayout()) {
          return 'aspect-ratio: ' + this.collapsedAspect() + ';';
        }
        const img = this.baseImage();
        if (!img || !img.width || !img.height) return 'aspect-ratio: 1 / 1;';
        return 'aspect-ratio: ' + img.width + ' / ' + img.height + ';';
      },

      // --- interaction ---
      /**
       * Every change re-renders AND re-syncs the cart forms.
       *
       * Keeping the hidden inputs continuously in sync (rather than only writing them when
       * the modal's Add to Bag is pressed) means a customer who types a name, closes the
       * modal, then uses the page's own Add to Bag still gets their personalisation. It also
       * removes a whole class of "form says one thing, preview says another" bugs.
       */
      /**
       * Every line's value, keyed the way the config's placements reference them.
       *
       * Assembled rather than stored, because the primary line lives in `text`. This is what the
       * geometry draws from and what the cart properties are written from, so there is exactly one
       * definition of "what did the customer type".
       */
      allTexts() {
        const out = {};
        for (const line of this.textLines()) {
          let value = this.textFor(line.key);
          // Gboard reports isComposing mid-word, so state can still be lowercase here
          if (value && line.uppercase_only === true) {
            value = window.PimConfig.toUppercaseValue(value, this.charLimit(line.key));
          }
          if (value) out[line.key] = value;
        }
        return out;
      },

      /**
       * A keystroke in one line's input.
       *
       * `key` is optional and defaults to the primary line, so a single-line product (and the
       * tests written against it) calls this exactly as before.
       */
      onTextInput(event, key) {
        const primary = this.primaryLine();
        const lineKey = key === undefined ? (primary && primary.key) || null : key;
        const limit = this.charLimit(lineKey);
        let v = event.target.value || '';

        // Emoji cannot be stitched, and the legacy widget strips them with this same
        // Extended_Pictographic class. Dropped in the rewrite, which let one through to
        // `_text_line_1`, the order and Odoo. Stripped rather than rejected so the customer
        // keeps the letters they typed, and flagged so the removal is not silent.
        const stripped = v.replace(/\p{Extended_Pictographic}/gu, '');
        this.emojiError = stripped !== v;
        this.emojiErrorKey = this.emojiError ? lineKey : null;
        if (this.emojiError) {
          v = stripped;
          event.target.value = v;
        }

        // After the strip and before the slice: capitals can lengthen the value (ß -> SS)
        if (this.uppercaseOnly(lineKey)) {
          v = window.PimConfig.applyUppercase(event.target, limit, event.isComposing);
        }

        if (limit && v.length > limit) v = v.slice(0, limit);

        if (primary && lineKey === primary.key && !this.isBundle()) {
          this.text = v;
        } else if (lineKey) {
          // Replaced rather than mutated in place: Alpine 3 proxies the object, and reassigning is
          // what a `x-for` over the keys can be relied on to notice.
          this.textValues = Object.assign({}, this.textValues, { [lineKey]: v });
        }

        if (v.trim() && lineKey) {
          this.validationErrors = Object.assign({}, this.validationErrors, { [lineKey]: false });
        }
        this.render();
        this.applyToForms();
      },
      emojiErrorFor(key) {
        return this.emojiError && this.emojiErrorKey === key;
      },
      validationErrorFor(key) {
        return !!this.validationErrors[key];
      },
      selectFont(value) {
        this.font = value;
        this.stepFonts = this.withLinkedSteps(this.stepFonts, value, (step) => this.fontChoices(step));
        this.render();
        this.applyToForms();
      },
      selectColour(value) {
        this.fontColour = value;
        this.stepColours = this.withLinkedSteps(this.stepColours, value, (step) => this.colourChoices(step));
        this.render();
        this.applyToForms();
      },

      /** Applies the pick to the active step and to steps sharing its lines that offer it, so per-line fonts agree. */
      withLinkedSteps(map, value, choicesOf) {
        const out = Object.assign({}, map);
        const active = this.previewStep;
        if (!active) return out;
        out[active.key] = value;

        const keys = new Set(this.stepTextLines(active).map((l) => l.key));
        if (!keys.size) return out;

        for (const step of this.allSteps()) {
          if (step.key === active.key) continue;
          const shares = this.stepTextLines(step).some((l) => keys.has(l.key));
          if (shares && choicesOf(step).some((c) => c.value === value)) out[step.key] = value;
        }
        return out;
      },
      /**
       * A REQUIRED product shows ONE button, per the Figma "PDP_personalizations is required"
       * frame: a single solid "Personalise & Add to Bag", with no separate Add to Bag beneath it.
       *
       * Done from JavaScript because only the fetched config knows whether a line is required, and
       * Liquid has already painted by then. That would normally risk a visible flash of the
       * theme's button, and does not here: `product-buy-buttons.liquid` renders Add to Bag
       * `disabled` with `data-atc-gate="pending"` until a personalisation widget releases it, so
       * the button the customer would glimpse is inert anyway, and this hides it before the gate's
       * failsafe can enable it.
       *
       * HIDDEN WITH AN INLINE STYLE, and that detail is the whole bug this replaces. The first
       * version set the `hidden` ATTRIBUTE, which relies on `[hidden] { display: none }` from
       * Tailwind's preflight. That rule is NOT in this theme's compiled stylesheet: grepping
       * staging's own `assets/styles.css` finds zero occurrences, while `.hidden` as a class is
       * there. So the attribute was set, changed nothing, and the customer saw two buttons.
       *
       * An inline style cannot lose to a theme rule, which matters here because the buy button
       * carries several and this must win against all of them.
       *
       * RE-APPLIED ON VARIANT CHANGE. The theme replaces the whole buy-button markup when the
       * variant changes, so the fresh buttons arrive visible and without our style. See the call
       * in watchVariantChanges.
       */
      applyRequiredLayout() {
        if (this.fallback || !this.requiresPersonalisation()) return;

        // Restore, not just skip. variant-select.js patches the existing button instead of
        // replacing it, so a `display: none` left from an earlier buyable variant would survive
        // the switch and leave the page with no buying control at all: our entry button hidden
        // by `variantAvailable`, and the theme's still hidden by us.
        //
        // Only ever restore a button THIS component hid. The theme also sets `display: none`
        // itself, for `show_add_to_cart` being off and for the shipping-insurance product, and
        // clearing that would reveal a button the merchant chose to hide.
        const available = this.selectedVariantAvailable();

        for (const button of this.themeAddToCartButtons()) {
          if (available) {
            if (button.style.display === 'none') continue;
            button.dataset.previewHidden = 'true';
            button.style.display = 'none';
          } else if (button.dataset.previewHidden === 'true') {
            delete button.dataset.previewHidden;
            button.style.display = '';
          }
        }
      },

      restoreThemeButtons() {
        for (const button of this.themeAddToCartButtons()) {
          if (button.dataset.previewHidden !== 'true') continue;
          delete button.dataset.previewHidden;
          button.style.display = '';
        }
      },

      /**
       * Is the CURRENTLY SELECTED variant buyable?
       *
       * Read off the theme's own add-to-cart button rather than computed here. The theme writes
       * `data-available` from `selected_or_first_available_variant.available` and re-renders the
       * buy buttons on every variant change, and it also disables the button for reasons we do
       * not model (quantity rules, a nil variant). Reusing its answer means the preview and the
       * theme can never disagree about whether a product can be bought.
       *
       * Absent button or attribute means the theme is not telling us, so assume buyable and
       * behave exactly as before this check existed.
       */
      selectedVariantAvailable() {
        // A bundle with an out-of-stock component is not buyable.
        const bundle = this.isBundle();
        if (bundle && typeof document !== 'undefined' && document.querySelector
          && document.querySelector('[data-bundle-oos="true"]')) {
          return false;
        }

        const buttons = this.themeAddToCartButtons();
        if (!buttons.length) return true;

        return buttons.some((button) => {
          if (button.dataset.available === 'false') return false;

          // On a bundle, `disabled` also means a size or component is still to be chosen.
          if (bundle) return true;

          // `disabled` catches what the attribute does not, such as the theme's quantity rule
          // (min order above stock). The one disabled state that is NOT a stock signal is the
          // add-to-cart gate, pending only until the widgets writing line-item properties load,
          // so reading it as sold out would blink the entry button away and back on every load.
          if (button.disabled && button.dataset.atcGate !== 'pending') return false;

          return true;
        });
      },

      /**
       * The theme's own add-to-bag buttons for THIS product, across every form it renders.
       *
       * Scoped through productForms() rather than by id: the template renders two forms sharing one
       * `product_form_id` plus the sticky bar, so an id lookup finds one and leaves the rest.
       */
      themeAddToCartButtons() {
        const out = [];
        for (const form of this.productForms()) {
          form.querySelectorAll('[type="submit"]').forEach((b) => out.push(b));
        }
        return out;
      },

      // ---------------------------------------------------------------------------
      // The reminder interstitial
      // ---------------------------------------------------------------------------

      /** Reminder copy, free or paid, from the same price answer the summary uses. */
      reminderTitle() {
        return this.isFreePersonalisation() ? this.labels.reminderTitle : this.labels.reminderTitlePaid;
      },

      reminderBody() {
        return this.isFreePersonalisation() ? this.labels.reminderBody : this.labels.reminderBodyPaid;
      },

      /** Answer the reminder by going to the modal. */
      reminderPersonalise() {
        this.reminderAnswered = true;
        this.reminderOpen = false;
        this.openModal();
      },

      /**
       * Answer the reminder by adding the item as it is.
       *
       * Forwards to the theme's own button rather than submitting the form, for the same reason
       * addToBag() does: the theme binds add-to-cart to the BUTTON's click and has no submit
       * handler, so requestSubmit would perform a plain browser navigation instead of the AJAX add.
       */
      reminderSkip() {
        this.reminderAnswered = true;
        this.reminderOpen = false;

        const forms = this.productForms();
        if (!forms.length) return;

        const form = forms.find((f) => f.offsetParent !== null) || forms[0];
        const button = form.querySelector('[type="submit"]');

        if (button) button.click();
      },

      /** Dismiss without answering: the customer gets asked again on the next attempt. */
      closeReminder() {
        this.reminderOpen = false;
        if (this._reminderReturnFocusTo && this._reminderReturnFocusTo.focus) {
          this._reminderReturnFocusTo.focus();
        }
      },

      setCollapsed(state) {
        if (this.collapsed === state) return;
        this.collapsed = state;
        this.render();
      },

      // ---------------------------------------------------------------------------
      // Modal chrome
      // ---------------------------------------------------------------------------

      /**
       * Scroll-driven collapse for the mobile preview, which is always pinned.
       *
       * Bound to the content pane (the element that actually scrolls inside the modal), not
       * the window — the modal locks body scroll, so window scroll never fires here. On mobile
       * that pane is the ONLY scrollable region: the preview above it does not move, which is
       * what the Figma frame named "Mobile - Simple - Pinned" describes.
       *
       * There is no unpin control, deliberately. The design has none, and a toggle that only
       * exists below 768px was an affordance we invented.
       */
      onPaneScroll(event) {
        if (!this.isMobileLayout()) return;

        const pane = event.target;
        const y = pane.scrollTop || 0;

        /*
         * HYSTERESIS PLUS A LATCH. This was `setCollapsed(scrollTop > 24)`, one threshold, and on
         * a real phone it shook the modal up and down without ever settling.
         *
         * The two states feed each other. Collapsing swaps the stage's aspect ratio from the
         * photo's natural one to `collapsedAspect()` (390/150), which on a 390px viewport hands
         * this pane about 240px of extra height. If the content cannot fill that, the browser
         * clamps `scrollTop` to 0, the old threshold read 0 and expanded, the stage grew back, the
         * still-moving finger pushed `scrollTop` over 24 again, and round it went. No threshold
         * value fixes that alone: the geometry swing is ten times the threshold.
         *
         * Two edges make the transition one-way: a deliberate 96px to collapse, a genuine return
         * to the top to expand. That is stable whenever the content stays scrollable after the
         * stage shrinks.
         *
         * `_collapseUnstable` covers the case where it does not. Rather than measure the stage and
         * predict the gain, we let the browser answer: collapse, then look on the next frame, and
         * if the clamp has put us at the top, undo it and LATCH so we never try again for this
         * stage. That makes the loop provably terminating, at the cost of one correction the
         * customer sees once. Cleared wherever the stage's height can change, so a different photo
         * or a taller pane gets a fresh chance.
         */
        if (!this.collapsed) {
          if (y <= 96 || this._collapseUnstable) return;

          this._collapsedAt = Date.now();
          this.setCollapsed(true);

          /*
           * Second line of defence only. The clamp usually announces itself as a scroll event,
           * which the branch below handles; this covers a browser that clamps WITHOUT firing one.
           * It cannot be the only check, because when the event does arrive it lands BEFORE this
           * callback and leaves `collapsed` false, so this returns early.
           */
          const settle = window.requestAnimationFrame || ((fn) => setTimeout(fn, 16));
          settle(() => {
            if (!this.collapsed) return;
            if ((pane.scrollTop || 0) > 0) return;
            this._collapseUnstable = true;
            this.setCollapsed(false);
          });
          return;
        }

        if (y <= 0) {
          /*
           * ARRIVING AT THE TOP WITHIN A FRAME OF COLLAPSING IS THE BROWSER, NOT THE CUSTOMER.
           * Collapsing hands this pane about 240px; when the content cannot fill it the scroll
           * position is clamped to 0 and that event is delivered in the same turn. Expanding on it
           * without latching is what let the shake resume: the finger is still moving, so it
           * crosses 96 again immediately. A real scroll back to the top, minutes later, must still
           * expand AND leave collapse available, which is what the elapsed-time test distinguishes.
           */
          if (this._collapsedAt && Date.now() - this._collapsedAt < 400) {
            this._collapseUnstable = true;
          }
          this.setCollapsed(false);
        }
      },

      /**
       * Collapse is a mobile-only behaviour — the pin control is `md:hidden`, so on desktop
       * the customer has no way to undo it. Gating here (and in stageStyle) stops a collapsed
       * state entered on a narrow viewport from sticking when the layout goes two-pane.
       * Matches Tailwind's `md` breakpoint.
       */
      /** Keeps a short bundle step scrollable after the photo collapses, so it cannot bounce. */
      paneContentStyle() {
        return this.isBundle() && this.isMobileLayout() ? 'min-height: calc(100% + 120px);' : '';
      },

      isMobileLayout() {
        return !(window.matchMedia && window.matchMedia('(min-width: 768px)').matches);
      },

      openModal() {
        // The widget owns the product now, and its own UI is on the page.
        if (this.fallback) return;

        // Normally already resolved (init started it), so this is just the guard against a
        // click that beats a slow network. Not awaited: the modal opens now and shows a
        // spinner in its content pane until the config lands.
        this.ensureConfig();
        this.ensureFonts();

        this.open = true;
        this._collapseUnstable = false;
        this.setCollapsed(false);

        // Remember what to return focus to, and stop the page scrolling behind the modal.
        this._returnFocusTo = document.activeElement;
        this._prevBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.classList.add('pim-modal-open');

        this.$nextTick(() => {
          this.render();
          this.focusNameInput();
          this.revealActiveStepTab();
        });
      },

      /**
       * Focus the FIRST text field, once the config has resolved (the fields live behind `ready`).
       *
       * Queried by attribute rather than through `$refs`: the inputs are now rendered by an `x-for`,
       * and an `x-ref` inside a template loop registers once per iteration into one slot, so the
       * ref would point at whichever line Alpine rendered last.
       */
      focusNameInput() {
        this.$nextTick(() => {
          const root = this.$el;
          const input = root && root.querySelector
            ? root.querySelector('[data-personalisation-text-input]')
            : null;
          // Without preventScroll the pane scrolls to the field and collapses the photo on open.
          if (input) input.focus({ preventScroll: true });
        });
      },

      closeModal() {
        this.open = false;
        document.body.style.overflow = this._prevBodyOverflow || '';
        document.documentElement.classList.remove('pim-modal-open');
        if (this._returnFocusTo && this._returnFocusTo.focus) this._returnFocusTo.focus();

        /**
         * RESET THE MOBILE CROP. Without this the preview stayed collapsed after the modal was
         * closed and reopened at the short "scrolled" strip instead of full size, which reads as
         * the picture never coming back after personalisation is added.
         *
         * It survived because the modal is `x-show`, not `x-if`: the SVG and its `stageStyle` are
         * the same nodes across open and close, so nothing rebuilt them. `collapsed` is view state
         * belonging to one visit, not to the customer's choices, so it is cleared on the way out
         * rather than on the way in — the pane's scrollTop is also 0 by then, so no scroll event
         * arrives to set it back.
         */
        this.setCollapsed(false);
      },

      /**
       * A required text line must be filled before anything is added to the bag.
       *
       * Called by addToBag(). It used to gate the step change instead, so removing the review
       * step would have removed the only validation the modal had.
       */
      canProceed() {
        // Every required line, not just the first: a product that asks for two names and takes an
        // order with one of them blank cannot be made.
        const ok = this.validateLines(this.textLines());

        if (!ok && this.hasMultipleSteps()) {
          const first = this.activeSteps().find((s) => this.stepHasError(s));
          if (first && (!this.previewStep || first.key !== this.previewStep.key)) this.selectStep(first.key);
        }

        return ok;
      },

      validateLines(lines) {
        const errors = {};
        let ok = true;

        for (const line of lines) {
          const missing = !!line.required && !this.textFor(line.key).trim();
          errors[line.key] = missing;
          if (missing) ok = false;
        }

        this.validationErrors = Object.assign({}, this.validationErrors, errors);
        return ok;
      },

      // ---------------------------------------------------------------------------
      // Pricing
      // ---------------------------------------------------------------------------

      formatMoney(cents) {
        if (window.Alpine && Alpine.store('xHelper') && Alpine.store('xHelper').formatMoney) {
          return Alpine.store('xHelper').formatMoney(cents, this.moneyFormat);
        }
        return (cents / 100).toFixed(2);
      },

      /**
       * Personalisation surcharge in cents for the active currency.
       *
       * `currency_prices` is keyed by currency code and is authoritative when present;
       * `price_gbp` is the base fallback. Most products price personalisation at zero, which
       * is why the design shows "For free".
       */
      personalisationCents() {
        // Checked before the price is even looked up, so every consumer (the summary, the
        // subtotal, and the cart property) inherits the market rule from one place.
        if (this.freePersonalisation) return 0;

        // Bundles price every filled line, like the legacy widget.
        if (this.isBundle()) {
          return this.textLines()
            .filter((line) => !!this.textFor(line.key).trim())
            .reduce((sum, line) => sum + this.lineCents(line), 0);
        }

        return this.lineCents(this.textLine());
      },

      lineCents(tl) {
        if (!tl) return 0;
        const perCurrency = tl.currency_prices && tl.currency_prices[this.currencyCode];
        const amount = perCurrency ? perCurrency.price : this.convertFromGbp(tl.price_gbp);
        return Math.round((Number(amount) || 0) * 100);
      },

      /**
       * GBP amount in the active currency, mirroring the legacy widget's
       * calculatePriceForCurrency() exactly, including its fallback.
       *
       * `price_gbp` is what its name says, so handing it straight to formatMoney() showed a
       * sterling number wearing another currency's symbol. Only reached when the config has no
       * explicit `currency_prices` entry for this currency; an explicit price always wins,
       * because merchandisers set those to hit round numbers rather than a converted price.
       *
       * An unknown currency returns the GBP figure unconverted, which is wrong but is what the
       * legacy widget does, and matching it beats inventing a second behaviour here.
       */
      convertFromGbp(priceGbp) {
        const amount = Number(priceGbp) || 0;
        if (!amount) return 0;
        const currencies = (this.config && this.config.currencies) || {};
        const data = currencies[this.currencyCode];
        const rate = data && Number(data.exchange_rate_from_gbp);
        return rate ? amount * rate : amount;
      },

      isFreePersonalisation() {
        return this.personalisationCents() === 0;
      },

      /**
       * Copy for the PDP entry button, from the same source as the summary's free/paid line.
       *
       * NOT just the market flag: a charge needs a paid market AND a non-zero PIM price, so
       * keying off the market alone prints "Add Personalisation" on a UK product that is
       * actually free, contradicting the summary right below it. Falls back to the market flag
       * until the config lands, which is what Liquid rendered, so it does not flicker.
       *
       * Never returns null: `x-text` coerces null to "", which would blank the button.
       */
      entryLabel() {
        // A required product has no separate Add to Bag, so this button is the only way to buy and
        // has to say so. Not gated on `ready` the way the free/paid choice is, because before the
        // config lands `requiresPersonalisation()` is false and the free/paid answer below is what
        // Liquid already painted.
        if (this.requiresPersonalisation() && this.labels.entryRequired) {
          return this.labels.entryRequired;
        }

        const free = this.ready ? this.isFreePersonalisation() : this.freePersonalisation;
        const wanted = free ? this.labels.entryFree : this.labels.entryPaid;
        return wanted || this.labels.entryPaid || this.labels.entryFree || '';
      },

      subtotalCents() {
        return (Number(this.itemPriceCents) || 0) + this.personalisationCents();
      },

      // ---------------------------------------------------------------------------
      // Cart wiring
      // ---------------------------------------------------------------------------

      /**
       * Write the selections into the product form as line-item properties.
       *
       * CROSS-SYSTEM CONTRACT — these exact property names are consumed downstream by the
       * `product-personalisation-fee` cart transform and by Engine's
       * OrderPersonalisationMapper on the way to Odoo. They are deliberately identical to
       * the ones the existing inline widget writes (personalisation-widget.js
       * updateFormInputs), so a product can be switched between the two UIs without
       * changing anything about the resulting order:
       *
       *   properties[_text_line_1]                    technical key -> raw value
       *   properties[<Label>]                          human-readable pair, shown in cart
       *   properties[_font] / properties[_font_colour] technical selections
       *   properties[_personalisation_applied]         marker = 1
       *   properties[_personalisation_total_modifier]  surcharge in CENTS (integer)
       *
       * Renaming any of these silently breaks fee calculation or the workshop's instructions.
       */
      applyToForms() {
        const forms = this.productForms();
        if (!forms.length) return;

        forms.forEach((f) =>
          f.querySelectorAll('[data-personalisation-input]').forEach((el) => el.remove())
        );

        const add = (name, value) => {
          forms.forEach((form) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[' + name + ']';
            input.value = value;
            input.setAttribute('data-personalisation-input', '');
            form.appendChild(input);
          });
        };

        const texts = this.allTexts();
        if (!Object.keys(texts).length) return;

        /**
         * A PAIR PER LINE: the technical key the fee transform and Engine read, and the label the
         * workshop and the customer read in the cart.
         *
         * Labels are deduped, because two lines can legitimately share one ("Name" on each of the
         * bunny's ears) and line-item properties are a MAP — the second would silently overwrite
         * the first and the workshop would be told to stitch one name. Numbered in placement order,
         * which is the order the fields appear in the modal.
         */
        const usedLabels = new Set();
        const bundle = this.isBundle();
        for (const line of this.textLines()) {
          const value = (texts[line.key] || '').trim();
          if (!value) continue;

          add(line.key, value);

          let label = this.lineLabel(line);
          if (bundle) label = this.prefixWithStep(this.lineStep(line), label);
          if (usedLabels.has(label)) {
            let n = 2;
            while (usedLabels.has(label + ' ' + n)) n++;
            label = label + ' ' + n;
          }
          usedLabels.add(label);
          add(label, value);
        }

        if (bundle) {
          this.applyBundleFontsToForms(add, texts);
        } else {
          if (this.font) {
            add('_font', this.font);
            add(this.labels.fontStyle, this.selectedFontLabel());
          }
          if (this.fontColour) {
            add('_font_colour', this.fontColour);
            add(this.labels.fontColour, this.selectedColourLabel());
          }
        }

        add('_personalisation_applied', '1');
        const cents = this.personalisationCents();
        if (cents > 0) add('_personalisation_total_modifier', String(cents));
      },

      prefixWithStep(step, label) {
        const name = this.stepLabel(step);
        return name ? name + ' - ' + label : label;
      },

      /**
       * Per-line font/colour for Engine's OrderPersonalisationMapper: `_font:text_line_N` and
       * `_font_colour:text_line_N`. The `_font` pair only when the bundle itself offers the value.
       */
      applyBundleFontsToForms(add, texts) {
        for (const line of this.textLines()) {
          if (!(texts[line.key] || '').trim()) continue;
          const code = line.label_key || String(line.key).replace(/^_/, '');
          const font = this.lineFont(line);
          const colour = this.lineColour(line);
          if (font) add('_font:' + code, font);
          if (colour) add('_font_colour:' + code, colour);
        }

        const steps = [];
        for (const line of this.textLines()) {
          if (!(texts[line.key] || '').trim()) continue;
          const step = this.lineStep(line);
          if (step && steps.indexOf(step) === -1) steps.push(step);
        }

        for (const step of steps) {
          const font = this.fontChoices(step).find((f) => f.value === this.stepFonts[step.key]);
          const colour = this.colourChoices(step).find((c) => c.value === this.stepColours[step.key]);
          if (font && this.stepElementEnabled(step, 'font')) {
            add(this.prefixWithStep(step, this.labels.fontStyle), font.label || font.value);
          }
          if (colour && this.stepElementEnabled(step, 'font_colour')) {
            add(this.prefixWithStep(step, this.labels.fontColour), colour.label || colour.value);
          }
        }

        const first = steps[0];
        if (!first) return;
        const bundleFonts = this.optionsBlock().fonts || [];
        const bundleColours = this.optionsBlock().font_colours || [];
        const firstFont = this.stepFonts[first.key];
        const firstColour = this.stepColours[first.key];
        if (firstFont && bundleFonts.some((f) => f && f.value === firstFont)) add('_font', firstFont);
        if (firstColour && bundleColours.some((c) => c && c.value === firstColour)) add('_font_colour', firstColour);
      },

      /**
       * Every add-to-cart form for THIS product.
       *
       * There are up to three: two layout branches of the main form plus the sticky bar
       * (product-template.liquid lines 947, 1225, 3154). Guarding or populating only the one
       * passed as `product_form_id` would leave the sticky bar as a bypass. The selector is
       * scoped by product id so other products' forms on the page (recommendations, quick
       * views) are untouched.
       */
      productForms() {
        if (!this.productId) return [];
        return Array.from(
          document.querySelectorAll(
            'form[data-type="add-to-cart-form"][data-product-id="' + this.productId + '"]'
          )
        );
      },

      requiresPersonalisation() {
        return this.textLines().some((line) => !!line.required);
      },

      /**
       * Personalisation counts as supplied once EVERY required line has text.
       *
       * Any-line-filled would let a customer fill one ear, press the page's own Add to Bag and
       * skip the other, which is an order the workshop cannot complete.
       */
      get personalisationApplied() {
        return this.textLines().every((line) => !line.required || !!this.textFor(line.key).trim());
      },

      /**
       * Gate add-to-cart when the product cannot be produced without personalisation.
       *
       * Without this, a customer could ignore the entry card, press the page's own
       * Add to Bag, and buy a required-personalisation product with nothing to embroider —
       * an order the workshop can't fulfil.
       *
       * Note the guards are attached after the config resolves, so a click in the first
       * moments of page load isn't gated. That matches the existing widget's behaviour.
       */
      validateForAddToCart() {
        if (this.fallback) return true;

        // Nothing typed yet, and the product does not insist: ask once before letting a plain
        // unpersonalised item through (Figma PDP_modal, "Don't miss free personalisation!").
        //
        // Only when the config is READY. Before that `textLines()` is empty, so `requiresPersonalisation`
        // is false and `hasAnyText` is false for every product, and a fast click would be met with a
        // reminder for a product that might not even offer personalisation.
        if (this.ready && !this.requiresPersonalisation()) {
          if (this.reminderAnswered || this.hasAnyText()) return true;

          this._reminderReturnFocusTo = document.activeElement;
          this.reminderOpen = true;
          return false;
        }

        if (!this.requiresPersonalisation()) return true;
        if (this.personalisationApplied) return true;

        this.openModal();
        // Marks the fields that are actually empty, so the message lands under the one the
        // customer still has to fill rather than under all of them.
        this.canProceed();
        return false;
      },

      /** Any text line with something in it, which is what makes a line "personalised". */
      hasAnyText() {
        return this.textLines().some((line) => !!this.textFor(line.key).trim());
      },

      /**
       * Mirrors the existing widget: a `submit` listener AND a capture-phase `click` guard on
       * the submit buttons. The click guard is the one that actually matters — the theme's
       * AJAX cart binds click, so blocking submit alone would be bypassed.
       */
      guardAddToCart() {
        for (const form of this.productForms()) {
          form.addEventListener('submit', (e) => {
            if (!this.validateForAddToCart()) {
              e.preventDefault();
              e.stopPropagation();
            }
          });

          form.querySelectorAll('button[type="submit"]').forEach((btn) => {
            btn.addEventListener(
              'click',
              (e) => {
                if (!this.validateForAddToCart()) {
                  e.preventDefault();
                  e.stopImmediatePropagation();
                }
              },
              { capture: true }
            );
          });
        }
      },

      /**
       * Commit the personalisation, then hand off to the theme's own add-to-cart.
       *
       * IT CLICKS THE BUTTON. It must not submit the form.
       *
       * This used to call `form.requestSubmit()`, on the assumption that it would run the theme's
       * AJAX cart the way a real click does. It does not: the theme binds add-to-cart to the
       * BUTTON's click (`@click="addToCart($event, true)"`, snippets/product-template.liquid:1052)
       * and has no `submit` handler on the form at all. So requestSubmit performed a plain browser
       * POST to `/cart/add`, and the browser followed Shopify's redirect to the cart PAGE — the item
       * was added correctly, but the customer was thrown off the product page instead of getting the
       * drawer. Clicking hands over to `addToCart`, which does the fetch and fires the theme's own
       * `eurus:cart:*` events, so the drawer, the popup and the counters all behave exactly as they
       * do for a non-personalised product. Nothing here needs to know how any of that works.
       *
       * `guardAddToCart` in this same file already said "the theme's AJAX cart binds click" — the
       * guard was written from that fact and this method was not.
       *
       * A VISIBLE form is preferred because the PDP renders the form up to three times (two layout
       * branches plus the sticky bar) and only one is on screen. Clicking a hidden one still adds the
       * item, but `addToCart` sets its loading state on that copy, so the customer would press the
       * button and see nothing happen.
       */
      addToBag() {
        if (!this.canProceed()) return;
        this.applyToForms();

        const forms = this.productForms();
        if (!forms.length) return;

        const form = forms.find((f) => f.offsetParent !== null) || forms[0];
        const button = form.querySelector('[type="submit"]');

        // The bundle widget keeps the button disabled until its options are chosen.
        if (this.isBundle() && this.bundleState && this.bundleState.valid === false) {
          this.bundleOptionsError = true;
          this.closeModal();
          this.revealBundleOptions();
          return;
        }

        this.closeModal();

        if (button) {
          button.click();
          return;
        }

        // Last resort only, and it is the behaviour described above: a form with no submit button
        // cannot be added to the cart by a customer either, so this should be unreachable.
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      },

      revealBundleOptions() {
        if (typeof document === 'undefined' || !document.querySelector) return;
        const empty = Array.from(document.querySelectorAll('[data-bundle-card-opt]')).find((el) => !el.value);
        const target = empty || this.bundleRoot();
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (empty && empty.focus) empty.focus({ preventScroll: true });
      },
    }));
  });
}
